import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Filter } from '@/components/Filters/Filter';
import {
  DEFAULT_VALUES,
  FILTERS
} from '@/components/Filters/Filters.constants';
import {
  ExpandedCategoryContext,
  RegionQueryContext
} from '@/components/Filters/FiltersUiState';
import { ParticipantTabs } from '@/components/ParticipantTabs/ParticipantTabs';
import { Button } from '@/components/ui/Button/Button';
import { CrossIcon } from '@/components/ui/Icons/Icons';
import { Overlay } from '@/components/ui/Overlay/Overlay';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { FilterTypeEnum } from '@/constants/common';
import { useBelow } from '@/hooks/useBelow';
import { FormValues, FormValuesKeys } from '@/types';
import { hasFormValuesChanged } from '@/utils/helpers/hasFormValuesChanged';

import * as S from './Filters.styles';

const id = 'filterFormId';

// Matches the drawer transform transition in Filters.styles.ts
const CLOSE_TRANSITION_MS = 350;

interface FiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: FormValues) => void;
  onReset?: () => void;
  externalMethods?: UseFormReturn<FormValues>;
}

function isExistingFormField(field: string): field is FormValuesKeys {
  const formKeys: FormValuesKeys[] = Object.keys(
    DEFAULT_VALUES
  ) as FormValuesKeys[];
  return formKeys.includes(field as FormValuesKeys);
}

export const Filters: FC<FiltersProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onReset,
  externalMethods
}) => {
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  // Mounting the filter widgets is expensive; doing it on click blocks the
  // open/close transition. Pre-mount them once the main thread is idle so
  // toggling the drawer only flips the CSS transform.
  const [isFormMounted, setIsFormMounted] = useState(false);
  // Transient widget UI state lifted out of the (permanently mounted) widgets
  // so it can be reset from one place when the drawer closes, instead of
  // relying on unmount. Kept in separate contexts so typing in the region
  // search doesn't re-render the expensive category tree.
  const [regionQuery, setRegionQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('');
  // Pending deferred form reset scheduled on close. Tracked so reopening (or
  // unmounting) within the close transition can cancel it before it wipes a
  // fresh interaction.
  const closeResetTimeoutRef = useRef<number | undefined>(undefined);
  const { t } = useTranslation();

  const regionQueryState = useMemo(
    () => [regionQuery, setRegionQuery] as const,
    [regionQuery]
  );
  const expandedCategoryState = useMemo(
    () => [expandedCategory, setExpandedCategory] as const,
    [expandedCategory]
  );

  const isMobile = useBelow('tablet');

  const internalMethods = useForm({
    defaultValues: DEFAULT_VALUES
  });

  const methods = externalMethods ?? internalMethods;

  const keys: FormValuesKeys[] = FILTERS.map(item => item.formField).flat(1);
  const formValues = methods.watch(keys);
  const hasValues = hasFormValuesChanged(formValues, DEFAULT_VALUES, keys);

  const { handleSubmit, setValue, reset } = methods;

  const submitCallback = async (data: FormValues) => {
    FILTERS.forEach(item => {
      if (item.type !== FilterTypeEnum.RANGE) return;
      const [fromField, toField] = item.formField;

      if (!isExistingFormField(fromField) || !isExistingFormField(toField))
        return;

      const fromValue = (data[fromField] || 0) as number;
      const toValue = (data[toField] || 0) as number;

      if (fromValue > toValue) {
        // eslint-disable-next-line no-param-reassign
        data = { ...data, [toField]: fromValue };
        setValue(toField, fromValue);
      }
    });

    onSubmit?.(data);
  };

  const handleCloseFilter = () => {
    onClose();
    // Resetting re-renders the heavy filter widgets through the form
    // subscriptions; defer it so the close transition starts instantly. Store
    // the timer so a quick reopen can cancel it (see the effect below).
    window.clearTimeout(closeResetTimeoutRef.current);
    closeResetTimeoutRef.current = window.setTimeout(
      () => reset(),
      CLOSE_TRANSITION_MS
    );
  };

  const handleResetFilter = () => {
    onReset?.();
    reset();
  };

  const toggleFilter = useCallback((key: string) => {
    setOpenFilterKey(prev => (prev === key ? null : key));
  }, []);

  useEffect(() => {
    if (isOpen) return;
    setOpenFilterKey(null);
  }, [isOpen]);

  // Reopening within the close transition must cancel the pending deferred
  // reset, otherwise it fires against the now-active form and clears the
  // selections the user just made.
  useEffect(() => {
    if (!isOpen) return;
    window.clearTimeout(closeResetTimeoutRef.current);
  }, [isOpen]);

  useEffect(() => () => window.clearTimeout(closeResetTimeoutRef.current), []);

  useEffect(() => {
    if (isOpen) return;
    // After any close (cross or overlay), clear the transient widget UI state
    // so the next open starts clean. Deferred until the close transition has
    // finished so re-rendering the (hidden) widgets never janks the animation;
    // the widgets stay mounted, preserving the open performance. Resetting to
    // the already-default values on the initial render is a no-op.
    const timeoutId = window.setTimeout(() => {
      setRegionQuery('');
      setExpandedCategory('');
    }, CLOSE_TRANSITION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsFormMounted(true);
      return;
    }

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(() => setIsFormMounted(true));
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(() => setIsFormMounted(true), 300);
    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  return (
    <>
      {isOpen && <Overlay onClick={onClose} zIndex={10} />}
      <S.Wrapper $isOpen={isOpen} onClick={e => e.stopPropagation()}>
        <S.HeaderWrapper>
          <S.Header>
            <S.CloseButton onClick={handleCloseFilter}>
              <CrossIcon />
            </S.CloseButton>

            <S.FilterTitle variant={isMobile ? 'h5' : 'h4'}>
              {t('Filter.title')}
            </S.FilterTitle>

            {isMobile && (
              <S.RightSection $isVisible={!!hasValues}>
                <Button variant="link" onClick={handleResetFilter}>
                  <Typography variant="link_15">
                    {t('Filter.reset_button')}
                  </Typography>
                </Button>
              </S.RightSection>
            )}
          </S.Header>

          {isMobile && <ParticipantTabs />}
        </S.HeaderWrapper>

        <S.Content>
          <FormProvider {...methods}>
            <form id={id} onSubmit={handleSubmit(submitCallback)}>
              {isFormMounted && (
                <RegionQueryContext.Provider value={regionQueryState}>
                  <ExpandedCategoryContext.Provider
                    value={expandedCategoryState}
                  >
                    {FILTERS.map(({ name, children, formField, type }) => (
                      <Filter
                        key={name}
                        title={name}
                        formField={formField}
                        type={type}
                        isOpen={openFilterKey === name}
                        onToggle={toggleFilter}
                      >
                        {children}
                      </Filter>
                    ))}
                  </ExpandedCategoryContext.Provider>
                </RegionQueryContext.Provider>
              )}
            </form>
          </FormProvider>
        </S.Content>

        <S.Footer>
          {/* Hide cancel button on screen < tablet */}
          {!isMobile && (
            <Button variant="secondary" onClick={handleResetFilter}>
              {t('Filter.reset_button')}
            </Button>
          )}

          <Button form={id} type="submit" variant="brand" fullWidth>
            {t('Filter.confirm_button')}
          </Button>
        </S.Footer>
      </S.Wrapper>
    </>
  );
};
